import * as BlazeSDK from '@wscsports/blaze-web-sdk'
import { debug } from '@/utils/debug'

// ─────────────────────────────────────────────────────────────────────────────
// Preset metadata — drives skeleton + Row/Grid widget dispatch in our code.
// ─────────────────────────────────────────────────────────────────────────────

export const WIDGET_KIND = {
  ROW: 'row',
  GRID: 'grid',
} as const
export type WscWidgetKind = (typeof WIDGET_KIND)[keyof typeof WIDGET_KIND]

export const SKELETON_SHAPE = {
  CIRCLE: 'circle',
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
} as const
export type WscSkeletonShape = (typeof SKELETON_SHAPE)[keyof typeof SKELETON_SHAPE]

export const CONTENT_TYPE = {
  STORY: 'story',
  MOMENT: 'moment',
} as const
export type WscContentType = (typeof CONTENT_TYPE)[keyof typeof CONTENT_TYPE]

// Web support status for a preset. Some BlazeSDK presets target the mobile SDK
// or expose UI quirks on the web — flag them here so the warning surfaces at
// runtime instead of silently rendering a broken layout.
export const WEB_SUPPORT = {
  FULL: 'full',
  MOBILE_ONLY: 'mobile-only',
  SDK_QUIRK: 'sdk-quirk',
} as const
export type WscWebSupport = (typeof WEB_SUPPORT)[keyof typeof WEB_SUPPORT]

export interface WscPresetConfig {
  widgetKind: WscWidgetKind
  skeletonShape: WscSkeletonShape
  gridColumns?: 2 | 3
  showLabel: boolean
  webSupport: WscWebSupport
  webSupportNote?: string
}

const { ROW, GRID } = WIDGET_KIND
const { CIRCLE, PORTRAIT, LANDSCAPE } = SKELETON_SHAPE
const { FULL, MOBILE_ONLY, SDK_QUIRK } = WEB_SUPPORT

export const WSC_PRESETS: Record<BlazeSDK.ThemeType, WscPresetConfig> = {
  'row-circle':                { widgetKind: ROW,  skeletonShape: CIRCLE,    showLabel: true,  webSupport: FULL },
  'row-rectangle':             { widgetKind: ROW,  skeletonShape: PORTRAIT,  showLabel: false, webSupport: FULL },
  'row-rectangle-horizontal':  { widgetKind: ROW,  skeletonShape: LANDSCAPE, showLabel: false, webSupport: FULL },
  'row-rectangle-animated':    { widgetKind: ROW,  skeletonShape: PORTRAIT,  showLabel: false, webSupport: SDK_QUIRK,   webSupportNote: 'SDK renders this as a circle on story content type instead of an animated rectangle.' },
  'grid-2-columns':            { widgetKind: GRID, skeletonShape: PORTRAIT,  gridColumns: 2,   showLabel: false, webSupport: MOBILE_ONLY, webSupportNote: 'Grid layouts are intended for mobile viewports.' },
  'grid-3-columns':            { widgetKind: GRID, skeletonShape: PORTRAIT,  gridColumns: 3,   showLabel: false, webSupport: MOBILE_ONLY, webSupportNote: 'Grid layouts are intended for mobile viewports.' },
  'grid-2-columns-horizontal': { widgetKind: GRID, skeletonShape: LANDSCAPE, gridColumns: 2,   showLabel: false, webSupport: MOBILE_ONLY, webSupportNote: 'Grid layouts are intended for mobile viewports.' },
  'grid-3-columns-horizontal': { widgetKind: GRID, skeletonShape: LANDSCAPE, gridColumns: 3,   showLabel: false, webSupport: MOBILE_ONLY, webSupportNote: 'Grid layouts are intended for mobile viewports.' },
  'default':                   { widgetKind: ROW,  skeletonShape: CIRCLE,    showLabel: true,  webSupport: FULL },
}

export const DEFAULT_WSC_PRESET: Record<WscContentType, BlazeSDK.ThemeType> = {
  [CONTENT_TYPE.STORY]: 'row-circle',
  [CONTENT_TYPE.MOMENT]: 'row-rectangle',
}

export const resolveWscPreset = (
  cardType: unknown,
  contentType: WscContentType
): BlazeSDK.ThemeType => {
  if (typeof cardType === 'string' && cardType in WSC_PRESETS) {
    const preset = cardType as BlazeSDK.ThemeType
    const config = WSC_PRESETS[preset]
    if (config.webSupport !== WEB_SUPPORT.FULL) {
      debug.warn(`[WSC] Preset "${preset}" (${contentType}) has limited web support: ${config.webSupport}. ${config.webSupportNote ?? ''}`)
    }
    return preset
  }
  return DEFAULT_WSC_PRESET[contentType]
}

// ─────────────────────────────────────────────────────────────────────────────
// BlazeSDK theme overrides — composed in 3 layers, applied via `configureTheme`:
//   1. COMMON_LAYOUT — applies to every preset.
//   2. SHAPE_LAYOUT[skeletonShape] — itemRatio + indicator placement per shape.
//   3. CONTENT_PLAYER[contentType] — story vs moment player chrome.
// ─────────────────────────────────────────────────────────────────────────────

const COMMON_LAYOUT = {
  labelStyle: {
    fontSize: "10px",
    color: "var(--shades-white)",
    font: "'Gilroy', sans-serif"
  },
  horizontalItemsSpacing: 24,
}

const THUMBNAIL_BORDER_INDICATORS = {
  statusUnreadStyle: {
    thumbnailBorderColor: 'var(--tertiary-500)',
    borderWidth: 2,
  },
  statusLiveStyle: {
    thumbnailBorderColor: 'var(--primary-500)',
    borderWidth: 2,
  },
}

const BADGE_INDICATORS = {
  statusUnreadStyle: {
    isVisible: true,
    font: "'Gilroy', sans-serif",
    backgroundColor: 'var(--tertiary-500)',
    borderColor: 'var(--tertiary-500)',
    textSize: '10px',
    borderRadius: '14px',
    padding: '4px 6px',
    borderWidth: '0',
  },
  statusLiveStyle: {
    isVisible: true,
    font: "'Gilroy', sans-serif",
    backgroundColor: 'var(--primary-500)',
    borderColor: 'var(--primary-500)',
    textSize: '10px',
    borderRadius: '14px',
    padding: '4px 6px',
    borderWidth: '0',
  },
}

const SHAPE_LAYOUT: Record<WscSkeletonShape, Record<string, unknown>> = {
  [CIRCLE]:    { itemRatio: 1,      ...THUMBNAIL_BORDER_INDICATORS },
  [PORTRAIT]:  { itemRatio: 2 / 3,  ...BADGE_INDICATORS },
  [LANDSCAPE]: { itemRatio: 16 / 9, ...BADGE_INDICATORS },
}

// Per-shape item dimensions used to size grid cells. Width is derived so a grid
// cell renders at the same physical card size as the matching row preset:
//   portrait  → height 240, aspect 2/3   → width 160
//   landscape → height 160, aspect 16/9  → width ~284
export const GRID_ITEM_WIDTH: Partial<Record<WscSkeletonShape, number>> = {
  [PORTRAIT]:  160,
  [LANDSCAPE]: 284,
}
export const GRID_GAP = 24

const CONTENT_PLAYER: Record<WscContentType, Record<string, unknown>> = {
  [CONTENT_TYPE.STORY]: {
    statusLiveStyle: { backgroundColor: "#FF364E" },
  },
  // BlazeSDK's `DefaultMomentStyle` playerStyle preset does not define
  // `statusLiveStyle` at all (only `DefaultStoryStyle` does). BlazeWidgetStory's
  // constructor unconditionally reads `this.playerStyle.statusLiveStyle.borderRadius`
  // (and `.padding`/`.font`/`.textColor`/`.borderWidth`) while building the LIVE
  // chip, regardless of content type. For story widgets that's covered by the
  // SDK's own `StatusLiveUnreadStyle` default; for moment widgets nothing fills
  // it in, so the very first read of `.borderRadius` throws
  // "Cannot read properties of undefined (reading 'statusLiveStyle')" during
  // widget construction. Provide the same full shape here so `configureTheme`'s
  // merge always leaves `playerStyle.statusLiveStyle` defined for moments too.
  // See FZ-WEB-NEXTJS-GY.
  [CONTENT_TYPE.MOMENT]: {
    statusLiveStyle: {
      backgroundColor: "#FF364E",
      textColor: "var(--shades-white)",
      font: "'Gilroy', sans-serif",
      textSize: '10px',
      borderRadius: '14px',
      borderWidth: 2,
      padding: '3px 9px',
    },
  },
}

const mergeInto = (target: any, source: Record<string, unknown>) => {
  Object.keys(source).forEach(key => {
    const value = source[key]
    if (typeof value === 'object' && value !== null) {
      target[key] = { ...target[key], ...value }
    } else {
      target[key] = value
    }
  })
}

export const configureTheme = (
  theme: any,
  contentType: WscContentType,
  shape: WscSkeletonShape
): void => {
  if (!theme?.layoutStyle || !theme?.playerStyle) return
  mergeInto(theme.layoutStyle, COMMON_LAYOUT)
  mergeInto(theme.layoutStyle, SHAPE_LAYOUT[shape])
  mergeInto(theme.playerStyle, CONTENT_PLAYER[contentType])
}
